<script setup>
import { computed } from 'vue';
import PipelineCardSkeleton from './PipelineCardSkeleton.vue';

const props = defineProps({
  columns: {
    type: Number,
    default: 4,
    validator: v => v >= 1 && v <= 10,
  },
  cardsPerColumn: {
    type: [Number, Array],
    default: 3,
    validator: v => {
      if (typeof v === 'number') return v >= 0 && v <= 10;
      if (Array.isArray(v)) return v.every(n => n >= 0 && n <= 10);
      return false;
    },
  },
});

const columnCardCounts = computed(() => {
  if (Array.isArray(props.cardsPerColumn)) {
    return Array.from(
      { length: props.columns },
      (_, i) => props.cardsPerColumn[i] ?? 3
    );
  }
  return Array.from({ length: props.columns }, (_, i) => {
    const baseCount = props.cardsPerColumn;
    const variations = [baseCount, baseCount - 1, baseCount + 1, baseCount];
    return Math.max(1, variations[i % variations.length]);
  });
});
</script>

<template>
  <div class="flex gap-4 p-3 min-h-[400px] overflow-x-auto">
    <div
      v-for="(cardCount, colIndex) in columnCardCounts"
      :key="colIndex"
      class="flex-none w-80 min-w-[280px] max-w-[350px] bg-n-alpha-1 rounded-xl flex flex-col animate-loader-pulse"
      :style="{ '--animation-delay': `${colIndex * 0.1}s` }"
    >
      <div
        class="flex items-center justify-between px-3 py-3 border-b border-n-weak"
      >
        <div class="flex items-center gap-2">
          <div
            class="w-2.5 h-2.5 rounded-full bg-n-alpha-1 animate-loader-pulse"
          />
          <div class="w-20 h-4 rounded bg-n-alpha-1 animate-loader-pulse" />
          <div class="w-6 h-5 rounded-full bg-n-alpha-1 animate-loader-pulse" />
        </div>
        <div class="flex gap-1">
          <div class="w-7 h-7 rounded-lg bg-n-alpha-1 animate-loader-pulse" />
        </div>
      </div>

      <div class="flex-1 flex flex-col gap-2 p-2 min-h-[200px] overflow-hidden">
        <PipelineCardSkeleton
          v-for="cardIndex in cardCount"
          :key="cardIndex"
          :style="{ '--card-delay': `${colIndex * 0.1 + cardIndex * 0.05}s` }"
        />
      </div>

      <div class="px-3 py-2 border-t border-n-weak">
        <div class="w-full h-9 rounded-lg bg-n-alpha-1 animate-loader-pulse" />
      </div>
    </div>
  </div>
</template>
